/**
 * The structured meeting schedule (H2 v2 build item 1).
 *
 * This is not a free-text "when does it meet" box. Every meeting is a date, a
 * start, an end and a location because four separate things depend on the
 * structure: the scheduled-hours total that must reconcile to the CCL approved
 * hours, the census date, the reporting period (taken from the END date), and
 * the schedule block printed on the Official Attendance Record (H8 §5).
 *
 * Presented as a real <table> because it is tabular data — a screen reader
 * announces the column for each cell, which a stack of <div>s would not.
 */

/** Advance a YYYY-MM-DD string by n days, staying in local time. */
function addDays(dateString, n) {
  const d = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateString;
  d.setDate(d.getDate() + n);
  const pad = (v) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function MeetingScheduleEditor({ meetings, onChange, disabled = false }) {
  const rows = meetings ?? [];

  function updateRow(index, patch) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeRow(index) {
    onChange(rows.filter((_, i) => i !== index));
  }

  /**
   * A new meeting copies the previous one a week later.
   *
   * Sections here run as weekly series far more often than not, so the common
   * case becomes "click add, click add, click add" instead of retyping the same
   * times and bay six times — which is also where transcription errors in the
   * hours total come from.
   */
  function addRow() {
    const previous = rows[rows.length - 1];
    onChange([
      ...rows,
      previous
        ? { ...previous, date: addDays(previous.date, 7) }
        : { date: '', start: '18:00', end: '21:00', location: '' },
    ]);
  }

  return (
    <div className="schedule">
      {rows.length === 0 ? (
        <p className="muted">
          No meetings yet. Add the dates this section actually meets — the scheduled hours,
          the census date and the reporting period are all worked out from them.
        </p>
      ) : (
        <div className="schedule__scroll">
          <table className="schedule__table">
            <caption className="visually-hidden">
              Meeting schedule: one row per meeting, with date, start time, end time and location.
            </caption>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Date</th>
                <th scope="col">Start</th>
                <th scope="col">End</th>
                <th scope="col">Location</th>
                <th scope="col">
                  <span className="visually-hidden">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                // Index as key is correct here: rows have no id, and the list is
                // only ever appended to or spliced, with all state held above.
                <tr key={index}>
                  <td className="muted">{index + 1}</td>
                  <td>
                    <input
                      type="date"
                      value={row.date ?? ''}
                      disabled={disabled}
                      aria-label={`Meeting ${index + 1} date`}
                      onChange={(e) => updateRow(index, { date: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={row.start ?? ''}
                      disabled={disabled}
                      aria-label={`Meeting ${index + 1} start time`}
                      onChange={(e) => updateRow(index, { start: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={row.end ?? ''}
                      disabled={disabled}
                      aria-label={`Meeting ${index + 1} end time`}
                      onChange={(e) => updateRow(index, { end: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.location ?? ''}
                      disabled={disabled}
                      placeholder="Station 5, Bay 2"
                      aria-label={`Meeting ${index + 1} location`}
                      onChange={(e) => updateRow(index, { location: e.target.value })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--quiet"
                      disabled={disabled}
                      onClick={() => removeRow(index)}
                    >
                      Remove<span className="visually-hidden"> meeting {index + 1}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="schedule__actions">
        <button type="button" className="btn" disabled={disabled} onClick={addRow}>
          Add meeting
        </button>
        {rows.length > 0 && (
          <span className="muted"> Each new meeting starts as a copy of the last one, a week later.</span>
        )}
      </p>
    </div>
  );
}

export default MeetingScheduleEditor;
